'use client';
import React, { useState } from 'react';

const capabilities = [
  {
    num: '[01]',
    title: 'SAST / DAST / SCA',
    description:
      'We integrate Static Analysis, Dynamic Analysis, and Software Composition Analysis directly into your CI/CD pipelines, so every commit is validated before it reaches production. No bolt-on scanning, no ignored reports. Findings come with context and remediation guidance.',
  },
  {
    num: '[02]',
    title: 'CVE Triage & Enrichment',
    description:
      'We do not just surface CVEs, we triage and enrich them with business context. Our engineers cut through scanner noise to identify which vulnerabilities pose real risk to your organisation, so your teams fix what matters and do not waste cycles on theoretical issues.',
  },
  {
    num: '[03]',
    title: 'Threat Modelling',
    description:
      'We embed threat modelling into your engineering process, reviewing new features, services, and infrastructure changes before they ship. Security designed in, not discovered after launch. Attack surface analysis, trust boundary mapping, and risk-prioritised findings.',
  },
  {
    num: '[04]',
    title: 'Risk-Led Security Programmes',
    description:
      'We build security programmes around your actual threat landscape, not generic frameworks. Every control, every policy, every process is prioritised by the risk it mitigates for your specific organisation, not because a checklist says so.',
  },
  {
    num: '[05]',
    title: 'SOC2 & ISO 27001',
    description:
      'From gap analysis to certification, and beyond. We implement controls, build audit-ready evidence, and stay embedded to ensure your compliance posture never lapses between audit windows. Continuous compliance, not annual scrambles.',
  },
  {
    num: '[06]',
    title: 'DORA Compliance',
    description:
      'We help financial services organisations meet Digital Operational Resilience Act requirements, ICT risk management frameworks, incident response and reporting procedures, third-party risk oversight, and digital resilience testing embedded in your DevSecOps practice.',
  },
  {
    num: '[07]',
    title: 'GDPR & Data Protection',
    description:
      'We implement data protection by design across your infrastructure and pipelines, privacy impact assessments, technical controls for data minimisation and access restriction, and engineering-level GDPR compliance that goes beyond policy documents.',
  },
  {
    num: '[08]',
    title: 'Continuous Monitoring',
    description:
      'Security is not a point-in-time activity. We provide continuous monitoring of your controls, pipelines, and infrastructure, detecting drift and responding before it becomes a finding or a breach. Every day, not just before the next audit.',
  },
  {
    num: '[09]',
    title: 'Pipeline & IaC Security',
    description:
      'We harden your CI/CD pipelines and secure your Infrastructure as Code, Terraform, Pulumi, CloudFormation, and Ansible, against misconfigurations and policy violations before they reach any environment. Policy-as-code enforcement with OPA, Checkov, and custom rulesets.',
  },
  {
    num: '[10]',
    title: 'Cloud Platform Security',
    description:
      'We design and implement secure cloud architectures across AWS, Azure, and GCP, covering identity and access management, network segmentation, workload protection, Kubernetes security, and cloud-native security controls. Security built in from day one.',
  },
  {
    num: '[11]',
    title: 'Embedded DevSecOps',
    description:
      'Our engineers work as part of your team, not as external consultants who drop in once a year. We integrate into your sprints, your standups, and your delivery process to keep security moving at engineering speed. Embedded, not adjacent.',
  },
];

export default function CapabilitiesSection() {
  const [activeSlide, setActiveSlide] = useState(0);
  const visibleCount = 3;
  const maxSlide = capabilities?.length - visibleCount;

  const handleNext = () => setActiveSlide((prev) => Math.min(prev + 1, maxSlide));
  const handlePrev = () => setActiveSlide((prev) => Math.max(prev - 1, 0));

  return (
    <section id="capabilities" className="bg-[#050a0f] border-t border-[#222] py-20 lg:py-28">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 mb-16">
          <div className="lg:w-1/2">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">
              Capabilities
            </p>
            <h2 className="font-heading text-[clamp(2rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight">
              Deep expertise across the full security stack
            </h2>
          </div>
          <div className="lg:w-1/2 flex items-end">
            <p className="text-[#aaa] text-base leading-relaxed">
              From SAST/DAST/SCA and CVE triage to threat modelling, DORA, GDPR, continuous compliance, and embedded DevSecOps, these are the capabilities TechSonar brings to every engagement. Delivered by senior engineers, embedded into your organisation, active every day of the year.
            </p>
          </div>
        </div>

        {/* Slider */}
        <div className="relative overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${activeSlide * (100 / visibleCount)}%)` }}
          >
            {capabilities?.map((cap, i) => (
              <div
                key={i}
                className="flex-shrink-0 w-full lg:w-1/3 pr-6"
              >
                <div className="capability-card p-8 h-full min-h-[280px] flex flex-col justify-between bg-[#181b1f]">
                  <div>
                    <div className="flex items-center gap-4 mb-6">
                      <span className="font-mono-brand text-[0.65rem] text-[#80edfa] tracking-widest">
                        {cap?.num}
                      </span>
                      <h3 className="font-heading text-lg text-[#f5f5f8] font-medium">
                        {cap?.title}
                      </h3>
                    </div>
                    <p className="text-sm text-[#aaa] leading-relaxed">{cap?.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between mt-8">
          <div className="flex gap-2">
            {Array.from({ length: maxSlide + 1 })?.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveSlide(i)}
                className={`w-2 h-2 transition-all duration-200 ${
                  activeSlide === i ? 'bg-[#80edfa]' : 'bg-[#333]'
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
          <div className="flex gap-3">
            <button
              onClick={handlePrev}
              disabled={activeSlide === 0}
              className="w-10 h-10 border border-[#222] flex items-center justify-center text-[#f5f5f8] disabled:opacity-30 hover:border-[#80edfa] transition-colors"
              aria-label="Previous slide"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button
              onClick={handleNext}
              disabled={activeSlide === maxSlide}
              className="w-10 h-10 border border-[#222] flex items-center justify-center text-[#f5f5f8] disabled:opacity-30 hover:border-[#80edfa] transition-colors"
              aria-label="Next slide"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
